// The workspace's currency and time zone, the two things every amount and every
// timestamp on screen depends on. Set once from the org settings the shell
// already loads (AuthedApp), read by lib/money and fmtZoned in lib/utils.
//
// Why a module value and not a prop: 55 money renders and 70 timestamps were
// hard-wired to rupees and India time, so WeightWorld (EUR, Europe/London) read
// "₹" on every figure. Threading the setting through each of them is how the
// next one gets missed.
//
// The last-seen values are kept per browser so a reload paints the right
// currency before the settings request returns.
const KEY = 'cb.workspaceDisplay';
const DEFAULTS = { currency: 'INR', timeZone: 'Asia/Kolkata' };

function load() {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || 'null');
    if (v && typeof v === 'object') return { ...DEFAULTS, ...v };
  } catch (_e) { /* private window or blocked storage: the defaults stand */ }
  return { ...DEFAULTS };
}

let current = load();

export function validZone(zone) {
  if (!zone) return false;
  try { new Intl.DateTimeFormat('en', { timeZone: zone }); return true; } catch (_e) { return false; }
}

function validCurrency(cur) {
  if (!/^[A-Z]{3}$/.test(String(cur || ''))) return false;
  try { new Intl.NumberFormat('en', { style: 'currency', currency: cur }); return true; } catch (_e) { return false; }
}

/**
 * Take the workspace's settings. Returns true when either value changed, so the
 * caller can re-render what was drawn with the old ones.
 */
export function setWorkspaceDisplay({ currency, timeZone } = {}) {
  const cur = String(currency || '').toUpperCase();
  const next = {
    currency: validCurrency(cur) ? cur : current.currency,
    timeZone: validZone(timeZone) ? timeZone : current.timeZone,
  };
  if (next.currency === current.currency && next.timeZone === current.timeZone) return false;
  current = next;
  try { localStorage.setItem(KEY, JSON.stringify(current)); } catch (_e) { /* not persisted: fine */ }
  return true;
}

export const workspaceCurrency = () => current.currency;
export const workspaceZone = () => current.timeZone;

/**
 * The workspace's calendar day of a timestamp, 'YYYY-MM-DD'. A day window is
 * the workspace's day: the server cuts it there, so a check in the browser must
 * too. Parsing '2026-10-16T00:00:00' is midnight in the BROWSER's zone, and an
 * Indian browser on a Chicago store kept 77 of yesterday's 162 orders (Trendia,
 * 17 Oct 2026).
 */
export function workspaceDay(ts, zone = current.timeZone) {
  // A bare calendar day (the API's DATE columns) is already the workspace's.
  if (typeof ts === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(ts)) return ts;
  const d = ts instanceof Date ? ts : new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  } catch (_e) { return d.toISOString().slice(0, 10); }
}

/**
 * The workspace's today as a local Date at midnight, for date labels and day
 * arithmetic beside numbers the server cut in the workspace's zone. A plain
 * `new Date()` set to midnight is the BROWSER's today: from India, a Chicago
 * store's "12 - 17 Oct" read "13 - 18 Oct" for the first ten and a half hours
 * of each Indian day.
 */
export function workspaceToday(zone = current.timeZone, now = new Date()) {
  const ymd = workspaceDay(now, zone);
  if (!ymd) { const d = new Date(now); d.setHours(0, 0, 0, 0); return d; }
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** 'YYYY-MM-DD' n days before the workspace's today ("Last 7 days" starts at workspaceDaysAgo(6)). */
export function workspaceDaysAgo(n, zone = current.timeZone, now = new Date()) {
  const ymd = workspaceDay(now, zone);
  if (!ymd) return null;
  const d = new Date(`${ymd}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

/** Whole workspace days from a timestamp to now: 0 today, 1 yesterday, -1 tomorrow. */
export function workspaceDaysSince(ts, zone = current.timeZone, now = new Date()) {
  const a = workspaceDay(ts, zone);
  const b = workspaceDay(now, zone);
  if (!a || !b) return null;
  return Math.round((new Date(`${b}T00:00:00Z`) - new Date(`${a}T00:00:00Z`)) / 86400000);
}
